import { Component, OnInit, OnDestroy } from '@angular/core';
import { AdminService } from '../../../service/admin.service';
import { PaginatorModule } from 'primeng/paginator';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { PrimengImports } from '../../../shared/primeng-imports';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { ViewUserDetailsComponent } from '../view-user-details/view-user-details.component';

@Component({
  selector: 'app-users-management-tab',
  standalone: true,
  imports: [CommonModule, PrimengImports, PaginatorModule, LucideIconsModule, FormsModule, ViewUserDetailsComponent],
  templateUrl: './users-management-tab.component.html',
  styleUrl: './users-management-tab.component.css',
  providers: [MessageService, ConfirmationService]
})
export class UsersManagementTabComponent implements OnInit, OnDestroy {
  users: any[] = [];
  isLoading = false;
  deletingUserId: string | null = null;
  resettingPasswordUserId: string | null = null;
  error = '';

  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  };

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // User details dialog
  showUserDetailsDialog: boolean = false;
  selectedUserId: string | null = null;

  // Reset password confirmation modal
  showResetPasswordModal: boolean = false;
  selectedUserForReset: any = null;

  // Archived users modal
  showArchivedUsersModal: boolean = false;
  archivedUsers: any[] = [];
  archivedUsersLoading = false;
  archivedUsersPagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  };
  archivedUsersSearchTerm: string = '';

  // Search timeout for archived users
  private searchTimeout: any;

  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.fetchUsers();
    this.searchSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pagination.currentPage = 1;
      this.fetchUsers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchUsers() {
    this.isLoading = true;
    this.error = '';
    this.adminService.getUsers(this.pagination.currentPage, this.pagination.itemsPerPage, this.searchTerm).subscribe({
      next: (data) => {
        this.users = data.users;
        this.pagination = data.pagination;
        this.isLoading = false;
      },
      error: (err) => {
        this.error = err?.message || 'Failed to load users.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.error, life: 3000 });
        this.isLoading = false;
      }
    });
  }

  onPageChange(event: any) {
    this.pagination.currentPage = event.page + 1;
    this.pagination.itemsPerPage = event.rows;
    this.fetchUsers();
  }

  onSearchInput(event: any): void {
    const value = event?.target?.value ?? '';
    this.searchTerm = value.trim();
    this.searchSubject.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.pagination.currentPage = 1;
    this.fetchUsers();
  }

  openUserDetailsDialog(userId: string): void {
    this.selectedUserId = userId;
    this.showUserDetailsDialog = true;
  }

  openArchivedUsersDialog() {
    this.showArchivedUsersModal = true;
    this.archivedUsersSearchTerm = '';
    this.archivedUsersPagination.currentPage = 1;
    this.fetchArchivedUsers();
  }

  fetchArchivedUsers() {
    this.archivedUsersLoading = true;
    this.adminService.getArchivedUsers(
      this.archivedUsersPagination.currentPage,
      this.archivedUsersPagination.itemsPerPage,
      this.archivedUsersSearchTerm
    ).subscribe({
      next: (data) => {
        this.archivedUsers = data.archivedUsers;
        this.archivedUsersPagination = data.pagination;
        this.archivedUsersLoading = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.message || 'Failed to load archived users.',
          life: 3000
        });
        this.archivedUsersLoading = false;
      }
    });
  }

  onArchivedUsersPageChange(event: any) {
    this.archivedUsersPagination.currentPage = event.page + 1;
    this.archivedUsersPagination.itemsPerPage = event.rows;
    this.fetchArchivedUsers();
  }

  onArchivedUsersSearchInput(event: any): void {
    const value = event?.target?.value ?? '';
    this.archivedUsersSearchTerm = value.trim();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.archivedUsersPagination.currentPage = 1;
      this.fetchArchivedUsers();
    }, 500);
  }

  clearArchivedUsersSearch(): void {
    this.archivedUsersSearchTerm = '';
    this.archivedUsersPagination.currentPage = 1;
    this.fetchArchivedUsers();
  }

  closeArchivedUsersModal() {
    this.showArchivedUsersModal = false;
    this.archivedUsers = [];
    this.archivedUsersSearchTerm = '';
  }

  confirmResetPassword(user: any) {
    this.selectedUserForReset = user;
    this.showResetPasswordModal = true;
  }

  cancelResetPassword() {
    this.showResetPasswordModal = false;
    this.selectedUserForReset = null;
    this.resettingPasswordUserId = null;
  }

  executeResetPassword() {
    if (!this.selectedUserForReset) return;

    this.resettingPasswordUserId = this.selectedUserForReset._id;

    this.adminService.resetUserPassword(this.selectedUserForReset._id).subscribe({
      next: (res) => {
        this.resettingPasswordUserId = null;

        if (res?.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res?.message || 'Temporary password has been sent to the user\'s email',
            life: 5000
          });
          // Close modal on success
          this.showResetPasswordModal = false;
          this.selectedUserForReset = null;
        } else {
          const msg = res?.message || 'Failed to reset password';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: msg,
            life: 5000
          });
        }
      },
      error: (err) => {
        this.resettingPasswordUserId = null;
        const msg = err?.message || 'Error resetting password';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: msg,
          life: 5000
        });
      }
    });
  }

  confirmDelete(user: any) {
    this.confirmationService.confirm({
      header: 'Delete User',
      message: `Are you sure you want to delete user "${user.fullName}"? This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg',
      rejectButtonStyleClass: 'bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg',
      accept: () => {
        this.deletingUserId = user._id;
        this.adminService.deleteUser(user._id).subscribe({
          next: (res) => {
            if (res?.success) {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'User deleted successfully' });
              this.fetchUsers();
            } else {
              const msg = res?.message || 'Failed to delete user';
              this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            }
            this.deletingUserId = null;
          },
          error: (err) => {
            const msg = err?.message || 'Error deleting user';
            this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
            this.deletingUserId = null;
          }
        });
      }
    });
  }
}
